/*
  global SignInWithApple
*/
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'lodash';
import helperMarkdownToComponent from 'helpers/Data';
import {
  Events,
  handleAnalytics,
  PageNames,
  SegmentPageStartSources,
} from '@foxcorp/delta-web-analytics';
import { isBrowserIE } from 'helpers/GlobalHelper';
import CTA from '../CTA';
import Header from '../Header';
import SocialLogin from '../SocialLogin';
import SignInAppleButton from '../SignInApple/components/SignInAppleButton';
import SignInSeparator from '../SignInSeparator';
import GoogleSignInButton from '../GoogleSignInButton';

import Style from './SignUp.module.scss';

const Signup = ({
  ctaProps,
  facebookLoginOptionalText,
  facebookLoginText,
  facebookGenericError,
  facebookSignupOptionalText,
  facebookSignupText,
  getUseUtagGlobal,
  handleShowShareMyEmailUpsell,
  headerProps,
  language,
  onClose,
  onFieldChange,
  onSignup,
  onSocialFacebookLogin,
  showFacebookSignup,
  pageViewEvent,
  privacyPolicyClickEvent,
  signUpEventFired,
  signupAgeError,
  signupEmail,
  signupEmailError,
  signupEmailExistsError,
  signupFirstName,
  signupFirstNameError,
  signupFullNameError,
  signupGenericError,
  signupLastName,
  signupLastNameError,
  signupPassword,
  signupPasswordError,
  signupShowPassword,
  signupSocialEmailExistsError,
  toggleCreatePassword,
  utagAccountSource,
}) => {
  const didTriggerSegmentProfileEvents = useRef(false);

  useEffect(() => {
    if (!didTriggerSegmentProfileEvents.current) {
      pageViewEvent({ pageName: PageNames.PROFILE_SIGN_UP, useUtagGlobal: getUseUtagGlobal() });

      handleAnalytics(
        {
          event: Events.PROFILE_SIGN_UP_LANDING,
          payload: {
            pageName: 'account:profile sign up',
            pageType: 'profile sign up',
          },
        },
        {
          event: Events.PROFILE_SIGNUP_STATRED,
          payload: { utagAccountSource },
        },
      );

      didTriggerSegmentProfileEvents.current = true;
    }
  }, [getUseUtagGlobal, pageViewEvent, utagAccountSource]);

  const {
    appleLoginText,
    profile_disclaimer: disclaimerText,
    profile_emailInvalidError: invalidEmailError,
    profile_existingErrorSignUpMessage: signUpExistingErrorMessage,
    profile_firstnameInputPlaceholder: firstNamePlaceholder,
    profile_genericErrorSignUpMessage: signUpErrorMessage,
    profile_lastnameInputPlaceholder: lastNamePlaceholder,
    profileCreate_createButton: createButtonText,
    profileCreate_emptyFirstAndLastNameError: emptyFirstAndLastNameError,
    profileCreate_emptyFirstNameError: firstNameEmptyError,
    profileCreate_emptyLastNameError: lastNameEmptyError,
    profileCreate_header: headerText,
    profileCreate_optinCombinedText: optinCombinedText,
    profileCreate_socialEmailExistsError: socialEmailExistsError,
    profileError_passwordLength: passwordLengthError,
  } = language || {};
  let signupPasswordInput = React.createRef();
  const passwordType = signupShowPassword ? 'text' : 'password';
  let errorText;
  let isFirstNameError;
  let isLastNameError;
  let isFullNameError;
  let isPasswordError;
  let isEmailError;

  if (signupGenericError) {
    const providedErrorMessaging = _.isString(signupGenericError) && signupGenericError;
    errorText = providedErrorMessaging || signUpErrorMessage;
  }

  if (signupFirstNameError && !signupFullNameError) {
    isFirstNameError = { active: true, text: firstNameEmptyError };
  }

  if (signupLastNameError && !signupFullNameError) {
    isLastNameError = { active: true, text: lastNameEmptyError };
  }

  if (signupFullNameError) {
    isFullNameError = { active: true, text: emptyFirstAndLastNameError };
  }

  if (signupEmailError) {
    isEmailError = { active: true, text: invalidEmailError };
  }

  if (signupEmailExistsError) {
    errorText = signUpExistingErrorMessage;
  }

  if (signupPasswordError) {
    isPasswordError = { active: true, text: passwordLengthError };
  }

  if (signupSocialEmailExistsError) {
    errorText = socialEmailExistsError;
  }
  const disclaimer = disclaimerText ? (
    <p className={Style.signupFieldInfo}>{disclaimerText}</p>
  ) : null;

  const submitHandler = e => {
    e.preventDefault();
    if (signUpEventFired) return false;
    onSignup();
    return false;
  };

  return (
    <main className={Style.SignUp}>
      <Header {...headerProps} />
      <div className={Style.SignUpContainer}>
        <h1>{headerText}</h1>
        <GoogleSignInButton onClose={onClose} toggleCreatePassword={toggleCreatePassword} />
        <SocialLogin
          facebookGenericError={facebookGenericError}
          facebookLoginOptionalText={facebookLoginOptionalText}
          facebookLoginText={facebookLoginText}
          facebookSignupOptionalText={facebookSignupOptionalText}
          facebookSignupText={facebookSignupText}
          onSocialFacebookLogin={onSocialFacebookLogin}
          showFacebookSignup={showFacebookSignup}
          showSignin
        />
        {SignInWithApple && !isBrowserIE() && (
          <SignInAppleButton
            handleSignIn={handleShowShareMyEmailUpsell}
            buttonText={appleLoginText}
          />
        )}
        <SignInSeparator />
        <div
          className={classNames(
            Style.errorText,
            (signupGenericError || signupEmailExistsError || signupSocialEmailExistsError) &&
              Style.active,
          )}
        >
          {errorText}
        </div>
        <form action="" method="post" autoComplete="off" onSubmit={submitHandler}>
          <div className={Style.signupNameContainer}>
            <div className={Style.signupColumnSplit}>
              <input
                className={classNames(
                  Style.inputField,
                  (isFirstNameError?.active || isFullNameError?.active) && Style.errorInput,
                )}
                type="text"
                placeholder={firstNamePlaceholder}
                name="signupFirstName"
                value={signupFirstName}
                onChange={onFieldChange}
                disabled={signupAgeError}
              />
              <div
                className={classNames(Style.errorText, isFirstNameError?.active && Style.active)}
              >
                {isFirstNameError?.text}
              </div>
            </div>
            <div className={Style.signupColumnSplit}>
              <input
                className={classNames(
                  Style.inputField,
                  (isLastNameError?.active || isFullNameError?.active) && Style.errorInput,
                )}
                type="text"
                name="signupLastName"
                placeholder={lastNamePlaceholder}
                value={signupLastName}
                onChange={onFieldChange}
                disabled={signupAgeError}
              />
              <div className={classNames(Style.errorText, isLastNameError?.active && Style.active)}>
                {isLastNameError?.text}
              </div>
            </div>
          </div>
          <div className={classNames(Style.errorText, isFullNameError?.active && Style.active)}>
            {isFullNameError?.text}
          </div>
          <div className={Style.signupColumn}>
            <input
              className={classNames(Style.inputField, isEmailError?.active && Style.errorInput)}
              type="email"
              name="signupEmail"
              placeholder="Email Address"
              value={signupEmail}
              onChange={onFieldChange}
              onInvalid={() => {
                handleAnalytics({
                  event: Events.PROFILE_SIGN_UP_ERROR,
                  payload: {
                    page_error_description: 'Invalid Email',
                    page_error_type: 'client-side',
                    page_start_source: SegmentPageStartSources.ONBOARDING,
                  },
                });
              }}
              autoCapitalize="none"
              disabled={signupAgeError}
            />
            <div className={classNames(Style.errorText, isEmailError?.active && Style.active)}>
              {isEmailError?.text}
            </div>
          </div>
          <div className={Style.signupColumn}>
            <div className={Style.signupInline}>
              <input
                className={classNames(
                  Style.inputField,
                  isPasswordError?.active && Style.errorInput,
                )}
                type={passwordType}
                name="signupPassword"
                placeholder="Password"
                value={signupPassword}
                onChange={onFieldChange}
                disabled={signupAgeError}
                ref={input => {
                  signupPasswordInput = input;
                }}
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <label className={Style.passwordLabel}>
                <input
                  className={classNames(Style.signupShowPassword)}
                  name="signupShowPassword"
                  type="checkbox"
                  value={signupShowPassword}
                  onChange={e => {
                    onFieldChange(e);
                    signupPasswordInput.focus();
                  }}
                  disabled={signupAgeError}
                />
                <span />
              </label>
            </div>
            <div className={classNames(Style.errorText, isPasswordError?.active && Style.active)}>
              {isPasswordError?.text}
            </div>
          </div>
          {disclaimer}
          <div
            className={classNames(Style.termsText, Style.signupTerms, Style.signupTermsContainer)}
          >
            <div className={Style.termsText}>
              {helperMarkdownToComponent(optinCombinedText, privacyPolicyClickEvent, {
                /* eslint-disable */
                Paragraph: props => {
                  const { children } = props;
                  if (!children) return null;
                  return <p className={Style.termsText}>{children}</p>;
                },
                Link: props => (
                  <a href={props.href} className={Style.termsLink} target="noopener noreferrer">
                    {props.children}
                  </a>
                ),
              })}
            </div>
          </div>
          <button className={Style.btn} disabled={signupAgeError}>
            {createButtonText}
          </button>
        </form>
        <CTA {...ctaProps} />
      </div>
    </main>
  );
};

export default Signup;

Signup.defaultProps = {
  backgroundClass: null,
  facebookGenericError: null,
  facebookLoginOptionalText: null,
  facebookLoginText: null,
  facebookSignupOptionalText: null,
  facebookSignupText: null,
  language: {},
  privacyPolicyClickEvent: {},
  showFacebookSignup: null,
  signupAgeError: null,
  signupBirthdate: null,
  signupBirthdateError: null,
  signupBirthdateInvalidError: null,
  signupBirthdatePlaceholder: null,
  signupEmail: '',
  signupEmailError: null,
  signupEmailExistsError: null,
  signupFirstName: '',
  signupFirstNameError: null,
  signupFullNameError: null,
  signupGenderDropdownOpen: null,
  signupGenderError: null,
  signupGenderVisualName: null,
  signupGenericError: null,
  signupLastName: '',
  signupLastNameError: null,
  signupPassword: '',
  signupPasswordError: null,
  signupShowPassword: false,
  signupSocialEmailExistsError: null,
};

Signup.propTypes = {
  backgroundClass: PropTypes.string,
  ctaProps: PropTypes.shape({}).isRequired,
  facebookGenericError: PropTypes.string,
  facebookLoginOptionalText: PropTypes.string,
  facebookLoginText: PropTypes.string,
  facebookSignupOptionalText: PropTypes.string,
  facebookSignupText: PropTypes.string,
  getUseUtagGlobal: PropTypes.func,
  headerProps: PropTypes.shape({}).isRequired,
  handleShowShareMyEmailUpsell: PropTypes.func,
  language: PropTypes.shape({
    profileCreate_header: PropTypes.string,
    profile_firstnameInputPlaceholder: PropTypes.string,
    profile_lastnameInputPlaceholder: PropTypes.string,
    profile_genericErrorSignUpMessage: PropTypes.string,
    profile_existingErrorSignUpMessage: PropTypes.string,
    profileCreate_optinCombinedText: PropTypes.string,
    profileCreate_createButton: PropTypes.string,
    profileError_passwordLength: PropTypes.string,
    profile_disclaimer: PropTypes.string,
  }),
  onAppleLinking: PropTypes.func,
  onAppleSignIn: PropTypes.func,
  onClose: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  onSignup: PropTypes.func.isRequired,
  onSocialFacebookLogin: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func,
  privacyPolicyClickEvent: PropTypes.shape({}),
  setAppleEmail: PropTypes.func,
  setAppleToken: PropTypes.func,
  setGender: PropTypes.func,
  setIsAppleEmailShared: PropTypes.func,
  showFacebookSignup: PropTypes.bool,
  signupAgeError: PropTypes.bool,
  signupBirthdate: PropTypes.string,
  signupBirthdateError: PropTypes.bool,
  signupBirthdateInvalidError: PropTypes.bool,
  signupBirthdatePlaceholder: PropTypes.string,
  signupEmail: PropTypes.string,
  signupEmailError: PropTypes.bool,
  signupEmailExistsError: PropTypes.string,
  signUpEventFired: PropTypes.bool,
  signupFirstName: PropTypes.string,
  signupFirstNameError: PropTypes.bool,
  signupFullNameError: PropTypes.bool,
  signupGenderDropdownOpen: PropTypes.bool,
  signupGenderError: PropTypes.bool,
  signupGenderVisualName: PropTypes.string,
  signupGenericError: PropTypes.string,
  signupLastName: PropTypes.string,
  signupLastNameError: PropTypes.bool,
  signupPassword: PropTypes.string,
  signupPasswordError: PropTypes.bool,
  signupShowPassword: PropTypes.bool,
  signupSocialEmailExistsError: PropTypes.string,
  toggleCreatePassword: PropTypes.func,
  toggleGender: PropTypes.func,
  utagAccountSource: PropTypes.string.isRequired,
};
